'use client'

// React Imports
import { useEffect, useState } from 'react'

// Next Imports
import { useParams, useRouter } from 'next/navigation'

// MUI Imports
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'

// Server Actions
import { getApprovedInvoices, getDueInvoices } from '@/app/server/supplierInvoices'

const CACHE_KEY = 'supplierInvoiceStats'
const CACHE_DURATION = 3 * 60 * 60 * 1000 // 3 hours in milliseconds

type CachedStats = {
  approvedCount: number
  dueCount: number
  timestamp: number
}

const SupplierInvoiceStats = () => {
  const [approvedCount, setApprovedCount] = useState<number>(0)
  const [dueCount, setDueCount] = useState<number>(0)
  const [loading, setLoading] = useState(true)

  const router = useRouter()
  const { lang: locale } = useParams()

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true)

        // Try to get cached data from localStorage
        const cachedData = localStorage.getItem(CACHE_KEY)

        if (cachedData) {
          const parsed: CachedStats = JSON.parse(cachedData)
          const now = Date.now()

          // Check if cache is still valid (less than 3 hours old)
          if (now - parsed.timestamp < CACHE_DURATION) {
            setApprovedCount(parsed.approvedCount)
            setDueCount(parsed.dueCount)
            setLoading(false)
            return
          }
        }

        // Cache is invalid or doesn't exist, fetch fresh data
        const [approvedResult, dueResult] = await Promise.all([getApprovedInvoices(), getDueInvoices()])

        const newApprovedCount = approvedResult.success ? approvedResult.count : 0
        const newDueCount = dueResult.success ? dueResult.count : 0

        setApprovedCount(newApprovedCount)
        setDueCount(newDueCount)

        // Store in localStorage with current timestamp
        const dataToCache: CachedStats = {
          approvedCount: newApprovedCount,
          dueCount: newDueCount,
          timestamp: Date.now()
        }
        localStorage.setItem(CACHE_KEY, JSON.stringify(dataToCache))
      } catch (error) {
        console.error('Error fetching supplier invoice stats:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  const handleApprovedClick = () => {
    // Use window.location for full page reload to ensure server component re-runs
    window.location.href = `/${locale}/supplierInvoices?status=Approved`
  }

  const handleDueClick = () => {
    // Use window.location for full page reload to ensure server component re-runs
    window.location.href = `/${locale}/supplierInvoices?status=Approved&due=true`
  }

  if (loading) {
    return (
      <Box display='flex' alignItems='center' gap={1}>
        <CircularProgress size={20} />
      </Box>
    )
  }

  return (
    <Box display='flex' alignItems='center' gap={1.5}>
      Supplier Invoices:
      <Tooltip title='Click to view Approved Invoices' arrow>
        <Chip
          label={approvedCount}
          color='success'
          size='small'
          variant='tonal'
          icon={<i className='bx-check-circle' />}
          onClick={handleApprovedClick}
          sx={{ cursor: 'pointer' }}
        />
      </Tooltip>
      <Tooltip title='Click to view Invoices Due in 3 Days' arrow>
        <Chip
          label={dueCount}
          color='error'
          size='small'
          variant='tonal'
          icon={<i className='bx-time-five' />}
          onClick={handleDueClick}
          sx={{ cursor: 'pointer' }}
        />
      </Tooltip>
    </Box>
  )
}

export default SupplierInvoiceStats
